import {
  AfterViewInit,
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  DestroyRef,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  Router,
  ActivatedRoute,
  ActivatedRouteSnapshot,
  RouterOutlet,
  NavigationEnd,
} from '@angular/router';
import { LocalTZtoTimezone } from '@core/constants/time.defaults';
import { DashboardControlButtonsService } from '@dashboard/components/dynamic-dashboard-wrapper/dashboard-control-buttons.service';
import { DynamicDashboardWrapperHelperService } from '@dashboard/components/dynamic-dashboard-wrapper/dynamic-dashboard-wrapper-helper.service';
import { ExtendButton } from '@dashboard/components/extend-more-buttons/extend-more-butons.types';
import { IStep } from '@dashboard/components/map-timeline/timeline.service';
import { TimezonesService } from '@dashboard/components/timezones/timezones.service';
import {
  ActiveTraceConfigSelector,
  FromDateSelector,
  MapClickAction,
  MapMoveAction,
  MapZoomAction,
  SelectedVesselLatLng,
  SelectedVesselSelector,
  SetActiveBaseMapLayer,
  SetActiveOverlayMapLayer,
  SetActiveTraceAction,
  SetOffsetStepAction,
  SetRightBar,
  TimezoneSelector,
  ToDateSelector,
  TraceConfigSelector,
  TraceLoadingSelector,
  VesselMetaSelector,
  VesselsSelector,
} from '@dashboard/dashboard-store';
import { VesselsHasError } from '@dashboard/dashboard-store/selectors/system-errors.selector';
import { StopWeatherTimerAction } from '@dashboard/dashboard-store/actions/weather.actions';
import { SystemDataErrorsService } from '@dashboard/services/system-data-errors.service';
import { VesselMarkersHelperService } from '@dashboard/services/vessel-markers-helper.service';
import { DashboardDateSyncService } from '@dashboard/services/dashboard-date-sync.service';
import { Store } from '@ngrx/store';
import { MapApiService } from '@widgets/map-widget/services/map-api.service';
import {
  combineLatest,
  combineLatestWith,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  interval,
  map,
  startWith,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouteIds, RouteStatics } from '@dashboard/dynamic-dashboards.route';
import { DefaultFromDateRouteParam } from '@dashboard/dashboard-store/reducers/ui-state.initial';
import { ThemeService } from '../../../../../services/theme.service';
import { HpDialogService } from '@shared/standalone-components/hp-dialog/hp-dialog.service';
import { StaleDateRangeDialogComponent } from '@dashboard/components/stale-date-range-dialog/stale-date-range-dialog.component';
import moment from 'moment';
@Component({
  selector: 'app-dashboard-container',
  standalone: false,
  templateUrl: './dashboard-container.component.html',
  styleUrl: './dashboard-container.component.scss',
})
export class DashboardContainerComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private _store = inject(Store);
  private router = inject(Router);
  private _activatedRoute = inject(ActivatedRoute);
  public helper = inject(DynamicDashboardWrapperHelperService);
  public _timezoneService = inject(TimezonesService);
  private _markersHelper = inject(VesselMarkersHelperService);
  private _mapService = inject(MapApiService);
  private DataErrorService = inject(SystemDataErrorsService);
  private controlsService = inject(DashboardControlButtonsService);
  private dateSync = inject(DashboardDateSyncService);
  private destroyRef = inject(DestroyRef);
  private dialogService = inject(HpDialogService);

  private activeVesselId?: string;
  private checkedInitialRouteDate = false;

  $timezoneData = this._timezoneService.timezoneData$;

  public activeVessel$ = this._store.select(SelectedVesselSelector).pipe(
    map((v) => {
      return !!v ? ('both' as const) : ('single' as const);
    })
  );
  public $DateFrom = this._store.select(FromDateSelector);
  public $DateTo = this._store.select(ToDateSelector);
  public TraceLoading$ = this._store
    .select(TraceLoadingSelector)
    .pipe(distinctUntilChanged());
  public ActiveTrace$ = this._store.select(ActiveTraceConfigSelector);
  public TraceConfig$ = this._store.select(TraceConfigSelector);

  // Tool buttons and map layer controls
  public $toolbuttons = this.controlsService.$toolsControls;
  public $mapLayerControls = this.controlsService.$mapLayerControls;

  // Right bar state
  public rightBar$ = this.helper.rightBar$;
  public $VesselsHasError = this._store.select(VesselsHasError);
  public $vesselCount = this._store
    .select(VesselsSelector)
    .pipe(map((vessels) => Object.values(vessels).length));

  public currentTime$ = interval(1000).pipe(
    startWith(0),
    map(() => new Date())
  );

  public tzDisplay$ = combineLatest([
    this.currentTime$,
    this._store.select(TimezoneSelector),
  ]).pipe(
    map(([now, tz]) => {
      const abs = Math.abs(tz);
      const h = Math.floor(abs);
      const m = Math.round((abs - h) * 60);
      const sign = tz >= 0 ? '+' : '-';
      const offsetStr =
        sign + String(h).padStart(2, '0') + String(m).padStart(2, '0');
      const label =
        tz === 0
          ? 'UTC'
          : `UTC${sign}${h}${m ? ':' + String(m).padStart(2, '0') : ''}`;
      return { now, offsetStr, label };
    })
  );

  toolsOpen = false;
  isToolsPage = false;
  isInfoPage = false;
  private sanitizer = inject(DomSanitizer);
  private themeService = inject(ThemeService);
  isDark$ = this.themeService.isDark$;
  toggleTheme() {
    this.themeService.toggle();
  }

  private readonly TOOL_ICONS: Record<string, string> = {
    Voyage_Consumption_Estimator: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 17 8 9 13 13 18 7"/><line x1="18" y1="4" x2="18" y2="10"/><line x1="15" y1="7" x2="21" y2="7"/></svg>`,
    Data_Export: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="13" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="16" x2="12" y2="21"/><polyline points="8 9 12 7 16 9"/><line x1="12" y1="7" x2="12" y2="13"/></svg>`,
    Fuel_Curves: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20 C7 20 7 4 12 4 C17 4 17 20 20 20"/><line x1="3" y1="20" x2="21" y2="20"/></svg>`,
    Wind_Assist_Experiments: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8h13a3 3 0 1 0-3-3"/><path d="M3 12h9"/><path d="M3 16h11a3 3 0 1 1-3 3"/></svg>`,
    Voyage_Experiments: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 3h6M9 3v8L4 21h16L15 11V3"/><circle cx="10" cy="15" r="1" fill="currentColor" stroke="none"/><circle cx="13" cy="18" r="1" fill="currentColor" stroke="none"/></svg>`,
    Engine_Cooling_System_Optimization: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"/></svg>`,
    Charter_Party_Agreement: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><polyline points="9 15 11 17 15 13"/></svg>`,
    Alarms_Monitoring_System: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>`,
    Fuel_Levels: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20V6a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v14"/><path d="M16 9h2a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-2"/><line x1="2" y1="20" x2="22" y2="20"/><line x1="7" y1="12" x2="13" y2="12"/></svg>`,
    Vessel_Reports: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>`,
    Noon_Reports: `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M12 2v1M12 13v1M4.9 5l.7.7M18.4 5l-.7.7M2 12h1M21 12h1"/><line x1="4" y1="20" x2="20" y2="20"/><line x1="7" y1="23" x2="17" y2="23"/></svg>`,
  };

  toolIcon(id: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(
      this.TOOL_ICONS[id] ??
        `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><circle cx="12" cy="12" r="9"/></svg>`
    );
  }

  private isRouteActive(path: string): boolean {
    let route: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    while (route) {
      if (route.routeConfig?.path === path) return true;
      route = route.firstChild;
    }
    return false;
  }

  @HostListener('document:click')
  closeToolsDropdown() {
    this.toolsOpen = false;
  }

  goToMap() {
    this.router.navigate(['/dashboard']);
  }

  async onToolClick(btn: ExtendButton) {
    this.toolsOpen = false;
    if (btn.id === 'Voyage_Consumption_Estimator' && this.isToolsPage) {
      const navigated = await this.router.navigate(['/dashboard']);
      if (!navigated) return;
    }

    if (btn.action) {
      btn.action();
    } else if (btn.url) {
      this.router.navigate([btn.url]);
    }
  }

  public navTabs = [{ label: 'HOME', active: true }];

  async ngOnInit() {
    this.helper.StartWSListener();

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        startWith(null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.isToolsPage = this.isRouteActive(RouteStatics.tools);
        this.isInfoPage = this.isRouteActive(RouteStatics.infoPageWithTab);
        this.navTabs[0].active = !this.isToolsPage;
      });

    const hasVesselRoute$ = this._activatedRoute.paramMap.pipe(
      map((params) => {
        return params.has(RouteIds.vesselImo);
      }),
      distinctUntilChanged()
    );
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        startWith(null),
        map(() => {
          const root = this.router.routerState.snapshot.root;

          let route = root;
          let hasVesselRoute = false;

          while (route) {
            if (route.routeConfig?.path === `:${RouteIds.vesselImo}`) {
              hasVesselRoute = true;
              break;
            }
            route = route.firstChild!;
          }

          const q = root.queryParamMap;

          return { q, hasVesselRoute };
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ q, hasVesselRoute }) => {
        const to = q.get('to');
        const from = q.get('from');

        this.openStaleDateDialogOnInitialLoad({
          from,
          to,
          hasVesselRoute,
        });
        this.dateSync.syncDateRangeFromNavigation(this._activatedRoute);
      });

    this.DataErrorService.InitIngestionErrorChecker();

    this._store.dispatch(MapZoomAction({ zoom: 3 }));

    this._mapService.SetZoomCallback = (zoom: number) => {
      this._store.dispatch(MapZoomAction({ zoom }));
    };

    this._mapService.SetMoveCallback = (bounds: L.LatLngBounds) => {
      this._store.dispatch(MapMoveAction({ bounds }));
    };

    // if (this.isDev) this.TestingAutoOpeningVesselPageToGraphsTab();
    // document.addEventListener('visibilitychange', () => {
    //   const tabIsActive = document.visibilityState === 'visible';
    //   if (tabIsActive) this._store.dispatch(TabIsActiveAction());
    // });
  }
  ngAfterViewInit(): void {
    this._markersHelper.RegisterCbs.RegisterClickCallBack = (id: string) =>
      this.clickMarker(id);

    this._markersHelper.RegisterCbs.RegisterMapClickCB = (latLng: L.LatLng) => {
      this._store.dispatch(MapClickAction({ latLng }));
    };
  }

  private async clickMarker(vesselId: string) {
    if (vesselId === this.activeVesselId) return;
    const vessel = await firstValueFrom(
      this._store.select(VesselMetaSelector(vesselId))
    );

    this.router.navigate([vessel.slug], {
      relativeTo: this.router.routerState.root.firstChild,
    });
  }

  mapButtonSelectHandler(event: { option: ExtendButton; groupIndex: number }) {
    const { option, groupIndex } = event;
    const { id } = option;

    switch (groupIndex) {
      case 1:
        this._store.dispatch(SetActiveOverlayMapLayer({ layerId: id }));
        break;

      case 0:
        this._store.dispatch(SetActiveBaseMapLayer({ layerId: id }));
        break;

      default:
        break;
    }
  }
  public async setToDateInterval(toDate: Date) {
    await this.dateSync.setToDateInterval(toDate, {
      syncRoute: true,
      route: this._activatedRoute,
    });
  }
  public async selectNewDate(dates: { from: Date; to: Date }) {
    await this.dateSync.setDateRangeFromPicker(dates, {
      syncRoute: true,
      route: this._activatedRoute,
    });
  }

  private openStaleDateDialogOnInitialLoad({
    from,
    to,
    hasVesselRoute,
  }: {
    from: string | null;
    to: string | null;
    hasVesselRoute: boolean;
  }): void {
    if (this.checkedInitialRouteDate) return;
    this.checkedInitialRouteDate = true;
    if (!to) return;

    const restoredTo = moment.parseZone(to);
    if (!restoredTo.isValid()) return;

    const latest = this.getLatestMinute();
    if (!restoredTo.utc().isBefore(latest)) return;

    const restoredFrom = from ? moment.parseZone(from) : null;

    this.dialogService.open(StaleDateRangeDialogComponent, {
      title: 'Navigating to specific dates',
      width: '420px',
      inputs: {
        from: restoredFrom?.isValid() ? restoredFrom.toDate() : undefined,
        to: restoredTo.toDate(),
        latest: latest.toDate(),
      },
      buttons: [
        {
          label: 'view latest',
          variant: 'primary',
          onClick: (ref) => {
            const latestTo = this.getLatestMinute().format('YYYY-MM-DDTHH:mm:ss');
            const latestFrom = hasVesselRoute
              ? DefaultFromDateRouteParam(latestTo)
              : undefined;
            this.dateSync.syncDateQueryParams(this._activatedRoute, {
              to: latestTo,
              ...(latestFrom ? { from: latestFrom } : {}),
            });
            this.dateSync.setDateRangeFromRouteParams({
              to: latestTo,
              ...(latestFrom ? { from: latestFrom } : {}),
            });
            ref.close();
          },
        },
        {
          label: 'keep selected time',
          variant: 'ghost',
          onClick: (ref) => ref.close(),
        },
      ],
    });
  }

  private getLatestMinute(): moment.Moment {
    return moment.utc().startOf('minute');
  }
  selectTrace(activeConfigId: number) {
    this._store.dispatch(SetActiveTraceAction({ activeConfigId }));
  }
  setStepSize(step: IStep) {
    this._store.dispatch(SetOffsetStepAction({ step }));
  }

  openList() {
    this._store.dispatch(SetRightBar({ selected: 'vesselSearch' }));
  }

  closeList() {
    this._store.dispatch(SetRightBar({ selected: '' }));
  }

  async vesseLatLng(): Promise<{ lat: string; lng: string }> {
    return await firstValueFrom(this._store.select(SelectedVesselLatLng));
  }

  ngOnDestroy(): void {
    this.helper.StopWsListener();

    this.DataErrorService.stopIngestionErrorChecker();
    this._store.dispatch(StopWeatherTimerAction());
  }
}
